if (!window.ShopifyFormsModalBase) {
window.ShopifyFormsModalBase = class ShopifyFormsModalBase extends HTMLElement {
  constructor() {
    super();

    this.formPanel = this.querySelector('[data-form-panel]');
    this.formFooter = this.querySelector('[data-form-footer]');
    this.successPanel = this.querySelector('[data-success-panel]');
    this.successFocusTarget = this.querySelector('[data-success-focus]');
    this.modalHeaderContent = this.querySelector('[data-modal-header-content]');
    this.errorBox = this.querySelector('[data-form-error]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.closeButton = this.querySelector('.modal-close');
    this.details = this.querySelector('details');
    this.detailsModal = this.querySelector('details-modal');
    this.summary = this.querySelector('summary');
    this.emailInput = this.querySelector('input[name="businessEmail"], input[name="email"]');
    this.phoneInput = this.querySelector('input[name="phone"]');
    this.urlInputs = Array.from(this.querySelectorAll('input[type="url"]'));

    this.extraInput = this.querySelector('[data-extra-field]');
    this.formTokenInput = this.querySelector('[data-form-token-field]');
    this.minSubmitIntervalMs = 2000;

    this.formHandle = (this.dataset.formHandle || '').trim().toLowerCase();
    this.activeSubmissionToken = null;
    this.activeAbortController = null;
    this.wasOpenedFromHash = false;

    this.defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent.trim() : 'Submit';
    this.loadingSubmitLabel = 'Submitting...';
    this.emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    this.phonePattern = /^\+?[0-9().\-\s]{10,25}$/;

    this.stringFields = [];
    this.booleanFields = [];
  }

  get _tag() {
    return this.tagName.toLowerCase();
  }

  get _openAttr() {
    return `data-${this._tag.replace(/-modal$/, '')}-open`;
  }

  get _targetDatasetKey() {
    return this._tag
      .replace(/-modal$/, '-target')
      .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  }

  connectedCallback() {
    if (!this.form) return;

    this.boundHandleSubmit = this.handleSubmit.bind(this);
    this.boundHandleEmailInput = this.handleEmailInput.bind(this);
    this.boundHandlePhoneInput = this.handlePhoneInput.bind(this);
    this.boundHandleUrlBlur = this.handleUrlBlur.bind(this);
    this.boundHandleDetailsToggle = this.handleDetailsToggle.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundHandleHashChange = this.handleHashChange.bind(this);

    this.form.addEventListener('submit', this.boundHandleSubmit);
    if (this.emailInput) this.emailInput.addEventListener('input', this.boundHandleEmailInput);
    if (this.phoneInput) this.phoneInput.addEventListener('input', this.boundHandlePhoneInput);
    this.urlInputs.forEach((input) => input.addEventListener('blur', this.boundHandleUrlBlur));
    if (this.details) this.details.addEventListener('toggle', this.boundHandleDetailsToggle);
    document.addEventListener('click', this.boundHandleDocumentClick);
    window.addEventListener('hashchange', this.boundHandleHashChange);
    window.requestAnimationFrame(() => this.handleHashChange({ isInitialLoad: true }));
  }

  disconnectedCallback() {
    if (this.form && this.boundHandleSubmit) {
      this.form.removeEventListener('submit', this.boundHandleSubmit);
    }
    if (this.emailInput && this.boundHandleEmailInput) {
      this.emailInput.removeEventListener('input', this.boundHandleEmailInput);
    }
    if (this.phoneInput && this.boundHandlePhoneInput) {
      this.phoneInput.removeEventListener('input', this.boundHandlePhoneInput);
    }
    if (this.boundHandleUrlBlur) {
      this.urlInputs.forEach((input) => input.removeEventListener('blur', this.boundHandleUrlBlur));
    }
    if (this.details && this.boundHandleDetailsToggle) {
      this.details.removeEventListener('toggle', this.boundHandleDetailsToggle);
    }
    this.cancelActiveSubmission();
    if (this.boundHandleDocumentClick) {
      document.removeEventListener('click', this.boundHandleDocumentClick);
    }
    if (this.boundHandleHashChange) {
      window.removeEventListener('hashchange', this.boundHandleHashChange);
    }
  }

  cancelActiveSubmission() {
    this.activeSubmissionToken = null;
    if (!this.activeAbortController) return;
    this.activeAbortController.abort();
    this.activeAbortController = null;
  }

  isActiveSubmission(token) {
    return this.activeSubmissionToken === token;
  }

  handleEmailInput() {
    this.validateEmail();
  }

  validateEmail() {
    if (!this.emailInput) return true;
    const value = this.emailInput.value.trim();
    this.emailInput.setCustomValidity('');
    if (!value) return true;
    if (!this.emailPattern.test(value)) {
      this.emailInput.setCustomValidity('Enter a valid email address.');
      return false;
    }
    return true;
  }

  handlePhoneInput() {
    this.validatePhone();
  }

  validatePhone() {
    if (!this.phoneInput) return true;
    const value = this.phoneInput.value.trim();
    this.phoneInput.setCustomValidity('');
    if (!value) return true;
    const digitsOnly = value.replace(/\D/g, '');
    if (!this.phonePattern.test(value) || digitsOnly.length < 10 || digitsOnly.length > 15) {
      this.phoneInput.setCustomValidity('Enter a valid phone number with 10 to 15 digits.');
      return false;
    }
    return true;
  }

  handleUrlBlur(event) {
    this.normalizeUrlInput(event.target);
  }

  normalizeUrlInputs() {
    this.urlInputs.forEach((input) => this.normalizeUrlInput(input));
  }

  normalizeUrlInput(input) {
    if (!input || input.disabled) return;
    const value = input.value.trim();
    if (!value || this.hasUrlScheme(value) || value.startsWith('//') || /\s/.test(value) || !value.includes('.')) {
      input.value = value;
      return;
    }
    input.value = `https://${value}`;
  }

  hasUrlScheme(value) {
    return /^[A-Z][A-Z0-9+.-]*:/i.test(value || '');
  }

  handleDetailsToggle() {
    if (!this.details || this.details.open) return;

    if (this.wasOpenedFromHash) {
      this.clearHashOpenTokens();
      this.wasOpenedFromHash = false;
    }

    this.cancelActiveSubmission();
    window.setTimeout(() => this.resetTransientState(), 180);
  }

  resetTransientState() {
    this.cancelActiveSubmission();
    this.clearError();
    this.setLoading(false);

    if (this.form) {
      this.form.reset();
      this.validateEmail();
      this.validatePhone();
    }

    if (this.formPanel) this.formPanel.classList.remove('hidden');
    if (this.successPanel) this.successPanel.classList.add('hidden');
    if (this.modalHeaderContent) this.modalHeaderContent.classList.remove('hidden');
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest(`[${this._openAttr}], a[href*="#${this._tag}"]`);
    if (!trigger) return;
    if (!this.isSamePageTrigger(trigger)) return;
    if (!this.matchesExternalTrigger(trigger)) return;

    event.preventDefault();
    if (this.syncHashFromTrigger(trigger)) return;
    this.openModal();
  }

  matchesExternalTrigger(trigger) {
    const explicitTarget = (trigger.dataset[this._targetDatasetKey] || '').trim().toLowerCase();
    const componentId = (this.id || '').trim().toLowerCase();
    const firstModal = document.querySelector(this._tag);

    if (explicitTarget) return explicitTarget === componentId;

    const triggerHashTokens = this.getTriggerHashTokens(trigger);
    if (triggerHashTokens.includes(this._tag)) return firstModal === this;
    if (componentId && triggerHashTokens.includes(componentId)) return true;
    if (trigger.hasAttribute(this._openAttr)) return firstModal === this;

    return false;
  }

  isSamePageTrigger(trigger) {
    if (!trigger || !trigger.hasAttribute('href')) return true;
    const href = (trigger.getAttribute('href') || '').trim();
    if (!href || href.startsWith('#')) return true;

    try {
      const triggerUrl = new URL(href, window.location.href);
      const currentUrl = new URL(window.location.href);
      if (triggerUrl.origin !== currentUrl.origin) return false;
      const normalizePath = (p) => String(p || '').replace(/\/+$/, '') || '/';
      if (normalizePath(triggerUrl.pathname) !== normalizePath(currentUrl.pathname)) return false;
      return !triggerUrl.search || triggerUrl.search === currentUrl.search;
    } catch {
      return false;
    }
  }

  decodeHashToken(token) {
    try {
      return decodeURIComponent(String(token || '')).trim().toLowerCase();
    } catch {
      return String(token || '').trim().toLowerCase();
    }
  }

  getHashTokens() {
    const rawHash = window.location.hash.replace(/^#/, '');
    if (!rawHash) return [];
    return rawHash.split('&').map((token) => this.decodeHashToken(token)).filter(Boolean);
  }

  getModalHashTokens() {
    const componentId = (this.id || '').trim().toLowerCase();
    const tokens = [this._tag];
    if (componentId) tokens.push(componentId);
    if (this.formHandle) tokens.push(this.formHandle, `${this._tag}-${this.formHandle}`);
    return Array.from(new Set(tokens));
  }

  getTriggerHashTokens(trigger) {
    const href = (trigger.getAttribute('href') || '').trim();
    if (!href) return [];
    let rawHash = '';
    if (href.startsWith('#')) {
      rawHash = href;
    } else {
      try { rawHash = new URL(href, window.location.href).hash; } catch { rawHash = ''; }
    }
    return rawHash.replace(/^#/, '').split('&').map((token) => this.decodeHashToken(token)).filter(Boolean);
  }

  hasHashOpenToken() {
    const activeTokens = this.getHashTokens();
    if (!activeTokens.length) return false;
    const componentId = (this.id || '').trim().toLowerCase();
    const firstModal = document.querySelector(this._tag);
    if (componentId && activeTokens.includes(componentId)) return true;
    if (activeTokens.includes(this._tag)) return firstModal === this;
    return false;
  }

  clearHashOpenTokens() {
    const modalTokens = new Set(this.getModalHashTokens());
    const remainingTokens = this.getHashTokens().filter((token) => !modalTokens.has(token));
    const nextHash = remainingTokens.length ? `#${remainingTokens.join('&')}` : '';
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
  }

  syncHashFromTrigger(trigger) {
    const triggerTokens = this.getTriggerHashTokens(trigger);
    if (!triggerTokens.length) return false;

    const modalTokens = new Set(this.getModalHashTokens());
    const existingNonModalTokens = this.getHashTokens().filter((token) => !modalTokens.has(token));
    const triggerHasNonModalTokens = triggerTokens.some((token) => !modalTokens.has(token));
    const nextTokens = Array.from(new Set(
      triggerHasNonModalTokens ? triggerTokens : [...existingNonModalTokens, ...triggerTokens]
    ));
    const currentTokens = this.getHashTokens();
    const hashDidChange =
      nextTokens.length !== currentTokens.length ||
      nextTokens.some((token, i) => token !== currentTokens[i]);

    if (!hashDidChange) {
      this.openModal({ openedFromHash: true });
      return true;
    }

    window.location.hash = nextTokens.join('&');
    return true;
  }

  handleHashChange(options = {}) {
    if (this.hasHashOpenToken()) {
      this.openModal({ openedFromHash: true });
      return;
    }

    if (!this.wasOpenedFromHash) return;
    this.wasOpenedFromHash = false;
    if (this.details && this.details.open) {
      this.closeModal(false);
    }
  }

  openModal(options = {}) {
    const { openedFromHash = false } = options;
    this.wasOpenedFromHash = openedFromHash;
    if (this.formTokenInput) this.formTokenInput.value = Date.now().toString();
    if (!this.summary) return;
    if (!this.details || !this.details.open) {
      this.summary.click();
    }
    window.setTimeout(() => {
      const focusTarget = openedFromHash
        ? this.closeButton || this.querySelector('[tabindex="-1"]')
        : this.form?.querySelector('input:not([type="hidden"]):not([disabled]):not([data-extra-field])');
      focusTarget?.focus();
    }, 120);
  }

  closeModal(focusToggle = false) {
    if (this.detailsModal && typeof this.detailsModal.close === 'function') {
      this.detailsModal.close(focusToggle);
      return;
    }
    if (this.details) this.details.removeAttribute('open');
  }

  shouldBlockSubmission() {
    if (this.extraInput && this.extraInput.value.trim() !== '') return true;
    if (this.formTokenInput) {
      const openedAt = Number(this.formTokenInput.value);
      if (openedAt && Date.now() - openedAt < this.minSubmitIntervalMs) return true;
    }
    return false;
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.clearError();

    if (this.shouldBlockSubmission()) {
      this.showSuccess();
      return;
    }

    this.normalizeUrlInputs();
    this.validateEmail();
    this.validatePhone();

    if (!this.form.reportValidity()) return;

    const endpointPath = (this.dataset.endpointPath || '').trim();
    if (!endpointPath) {
      this.showError('The form submission endpoint is not configured.');
      return;
    }

    const captchaProvider = (this.dataset.captchaProvider || 'none').trim();
    const turnstileToken = this.getTurnstileToken();
    if (captchaProvider === 'turnstile' && !turnstileToken) {
      this.showError('Complete the security check before submitting the form.');
      return;
    }

    const payload = this.buildPayload();
    const url = this.buildSubmitUrl(endpointPath);
    const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json' };
    if (turnstileToken) headers['X-Turnstile-Token'] = turnstileToken;

    const requestToken = Symbol(`${this._tag}-submission`);
    const abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    this.cancelActiveSubmission();
    this.activeSubmissionToken = requestToken;
    this.activeAbortController = abortController;
    this.setLoading(true);

    try {
      const requestOptions = { method: 'POST', headers, credentials: 'same-origin', body: JSON.stringify(payload) };
      if (abortController) requestOptions.signal = abortController.signal;

      const response = await fetch(url.toString(), requestOptions);
      if (!this.isActiveSubmission(requestToken)) return;

      const responseData = await this.parseResponse(response);
      if (!this.isActiveSubmission(requestToken)) return;

      if (!response.ok) {
        throw new Error(responseData.error || responseData.message || 'We could not submit the form right now.');
      }

      if (responseData.submitted !== true) {
        throw new Error('The form request completed but did not return a successful submission state.');
      }

      this.showSuccess();
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      if (!this.isActiveSubmission(requestToken)) return;
      this.showError(error.message || 'We could not submit the form right now.');
    } finally {
      if (!this.isActiveSubmission(requestToken)) return;
      this.activeSubmissionToken = null;
      this.activeAbortController = null;
      this.setLoading(false);
    }
  }

  buildSubmitUrl(endpointPath) {
    const url = new URL(endpointPath, window.location.origin);
    const targetEndpoint = (this.dataset.targetEndpoint || '').trim();
    const formHandle = (this.dataset.formHandle || '').trim();
    if (targetEndpoint) url.searchParams.set('target', targetEndpoint);
    if (formHandle) url.searchParams.set('form', formHandle);
    return url;
  }

  buildPayload() {
    const payload = {};

    this.stringFields.forEach((field) => {
      const control = this.form.elements.namedItem(field);
      if (!control || typeof control.value !== 'string') return;
      payload[field] = control.value.trim();
    });

    this.booleanFields.forEach((field) => {
      const value = this.getBooleanFieldValue(field);
      if (value !== null) payload[field] = value;
    });

    return payload;
  }

  getBooleanFieldValue(fieldName) {
    if (!this.form) return null;
    const control = this.form.elements.namedItem(fieldName);
    if (!control) return null;
    if (typeof RadioNodeList !== 'undefined' && control instanceof RadioNodeList) {
      if (control.value === '') return null;
      return control.value === 'true';
    }
    if (control.type === 'checkbox') return control.checked;
    return null;
  }

  async parseResponse(response) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) return response.json();
    const text = await response.text();
    return text ? { error: text } : {};
  }

  getTurnstileToken() {
    const tokenField = this.form ? this.form.elements.namedItem('cf-turnstile-response') : null;
    return tokenField && typeof tokenField.value === 'string' ? tokenField.value.trim() : '';
  }

  setLoading(isLoading) {
    if (!this.submitButton || !this.submitLabel) return;
    this.submitButton.disabled = isLoading;
    this.submitLabel.textContent = isLoading ? this.loadingSubmitLabel : this.defaultSubmitLabel;
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.classList.remove('hidden');
    this.errorBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.textContent = '';
    this.errorBox.classList.add('hidden');
  }

  showSuccess() {
    if (this.formPanel) this.formPanel.classList.add('hidden');
    if (this.modalHeaderContent) this.modalHeaderContent.classList.add('hidden');
    if (this.successPanel) {
      this.successPanel.classList.remove('hidden');
      this.successPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    const focusTarget = this.successFocusTarget || this.closeButton;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      window.requestAnimationFrame(() => {
        try { focusTarget.focus({ preventScroll: true }); } catch { focusTarget.focus(); }
      });
    }
  }
};
}
